import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    Paper,
    RadioGroup,
    Radio,
    FormControlLabel,
    FormControl,
    FormLabel,
    TextField,
    Tooltip,
    IconButton,
    Menu,
    MenuItem,
    Snackbar,
    Alert,
    Switch,
    FormGroup,
    Divider
} from '@mui/material';
import {
    Download,
    Code,
    Copy,
    HelpCircle,
    FileText,
    RefreshCw,
    Phone,
    Tablet,
    Laptop,
    ChevronDown,
    File,
    Printer,
    CheckCircle,
    QrCode,
    Calendar
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from "jspdf";
// Fixed import - use QRCodeSVG for React components
import { QRCodeSVG } from 'qrcode.react';

const HtmlPreviewTab = ({ 
    profileData, 
    generateProfilePageContent, 
    isTrial, 
    exportFilename, 
    setExportFilename, 
    getProfileSlug 
}) => {
    const [previewFormat, setPreviewFormat] = useState('html');
    const [previewContent, setPreviewContent] = useState('');
    const [deviceView, setDeviceView] = useState('desktop'); // desktop, tablet, mobile
    const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
    const [templateMenuAnchor, setTemplateMenuAnchor] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState('modern');
    const [includeQrCode, setIncludeQrCode] = useState(false);
    const [includeMeetingScheduler, setIncludeMeetingScheduler] = useState(false);
    const [meetingSchedulerUrl, setMeetingSchedulerUrl] = useState('');
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const iframeRef = useRef(null);
    const qrCodeRef = useRef(null);
    const navigate = useNavigate();

    // Available templates
    const templates = [
        { id: 'modern', name: 'Modern', color: '#2563eb' },
        { id: 'classic', name: 'Classic', color: '#4B5563' },
        { id: 'minimal', name: 'Minimal', color: '#10B981' },
        { id: 'dark', name: 'Dark Mode', color: '#1F2937' },
        { id: 'creative', name: 'Creative', color: '#8B5CF6' }
    ];

    // Template CSS definitions
    const templateStyles = {
        modern: `
            body {
                font-family: 'Segoe UI', Arial, sans-serif;
                color: #1F2937;
                background-color: #F9FAFB;
            }
            h1, h2, h3, h4, h5, h6 {
                color: #2563EB;
            }
            a {
                color: #2563EB;
                text-decoration: none;
            }
            a:hover {
                text-decoration: underline;
            }
            .header {
                background: linear-gradient(135deg, #2563EB 0%, #1E40AF 100%);
            }
            .qr-code-section {
                margin: 40px 0; 
                text-align: center; 
                padding: 20px; 
                background-color: #f0f5ff; 
                border-radius: 12px;
                border: 1px solid #dbeafe;
            }
            .meeting-scheduler-section {
                margin: 40px 0; 
                text-align: center; 
                padding: 20px; 
                background-color: #f0fdf4; 
                border-radius: 12px;
                border: 1px solid #dcfce7;
            }
            .meeting-btn {
                display: inline-block;
                background-color: #2563eb;
                color: white;
                padding: 12px 24px;
                border-radius: 6px;
                font-weight: 600;
                text-decoration: none;
                margin-top: 12px;
                transition: all 0.2s;
            }
            .meeting-btn:hover {
                background-color: #1d4ed8;
                transform: translateY(-2px);
                box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            }
        `,
        classic: `
            body {
                font-family: 'Times New Roman', serif;
                color: #111827;
                background-color: #F3F4F6;
            }
            h1, h2, h3, h4, h5, h6 {
                color: #4B5563;
                font-family: Georgia, serif;
            }
            a {
                color: #4B5563;
                text-decoration: underline;
            }
            .header {
                background: #E5E7EB;
            }
            .qr-code-section {
                margin: 40px 0; 
                text-align: center; 
                padding: 20px; 
                background-color: #f8fafc; 
                border-radius: 8px;
                border: 1px solid #e2e8f0;
            }
            .meeting-scheduler-section {
                margin: 40px 0; 
                text-align: center; 
                padding: 20px; 
                background-color: #f8fafc; 
                border-radius: 8px;
                border: 1px solid #e2e8f0;
            }
            .meeting-btn {
                display: inline-block;
                background-color: #4B5563;
                color: white;
                padding: 12px 24px;
                border-radius: 4px;
                font-weight: 600;
                text-decoration: none;
                margin-top: 12px;
                font-family: Georgia, serif;
            }
            .meeting-btn:hover {
                background-color: #374151;
            }
        `,
        minimal: `
            body {
                font-family: 'Arial', sans-serif;
                color: #374151;
                background-color: #FFFFFF;
            }
            h1, h2, h3, h4, h5, h6 {
                color: #10B981;
                font-weight: 400;
            }
            a {
                color: #10B981;
                text-decoration: none;
            }
            .header {
                background: #ECFDF5;
            }
            .qr-code-section {
                margin: 40px 0; 
                text-align: center; 
                padding: 20px; 
                background-color: #f9fafb; 
                border-radius: 4px;
            }
            .meeting-scheduler-section {
                margin: 40px 0; 
                text-align: center; 
                padding: 20px; 
                background-color: #ecfdf5; 
                border-radius: 4px;
            }
            .meeting-btn {
                display: inline-block;
                background-color: #10B981;
                color: white;
                padding: 10px 20px;
                border-radius: 2px;
                font-weight: 500;
                text-decoration: none;
                margin-top: 12px;
            }
            .meeting-btn:hover {
                background-color: #059669;
            }
        `,
        dark: `
            body {
                font-family: 'Segoe UI', Arial, sans-serif;
                color: #E5E7EB;
                background-color: #1F2937;
            }
            h1, h2, h3, h4, h5, h6 {
                color: #FFFFFF;
            }
            a {
                color: #60A5FA;
                text-decoration: none;
            }
            .header {
                background: #111827;
            }
            .skill-tag {
                background-color: #374151 !important;
                color: #F3F4F6 !important;
            }
            .qr-code-section {
                margin: 40px 0; 
                text-align: center; 
                padding: 20px; 
                background-color: #111827; 
                border-radius: 12px;
                border: 1px solid #374151;
            }
            .meeting-scheduler-section {
                margin: 40px 0; 
                text-align: center; 
                padding: 20px; 
                background-color: #111827; 
                border-radius: 12px;
                border: 1px solid #374151;
            }
            .meeting-btn {
                display: inline-block;
                background-color: #3B82F6;
                color: white;
                padding: 12px 24px;
                border-radius: 6px;
                font-weight: 600;
                text-decoration: none;
                margin-top: 12px;
            }
            .meeting-btn:hover {
                background-color: #2563EB;
            }
        `,
        creative: `
            body {
                font-family: 'Segoe UI', Arial, sans-serif;
                color: #4B5563;
                background-color: #F5F3FF;
            }
            h1, h2, h3, h4, h5, h6 {
                color: #8B5CF6;
                font-weight: 600;
            }
            a {
                color: #8B5CF6;
                text-decoration: none;
                background: linear-gradient(to right, #8B5CF6, #6D28D9);
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
            }
            .header {
                background: linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%);
            }
            .qr-code-section {
                margin: 40px 0; 
                text-align: center; 
                padding: 20px; 
                background-color: #f5f3ff; 
                border-radius: 16px;
                border: 1px dashed #d8b4fe;
                box-shadow: 0 4px 20px rgba(139, 92, 246, 0.1);
            }
            .meeting-scheduler-section {
                margin: 40px 0; 
                text-align: center; 
                padding: 20px; 
                background-color: #ede9fe; 
                border-radius: 16px;
                border: 1px dashed #c4b5fd;
                box-shadow: 0 4px 20px rgba(139, 92, 246, 0.1);
            }
            .meeting-btn {
                display: inline-block;
                background: linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%);
                color: white;
                padding: 12px 30px;
                border-radius: 30px;
                font-weight: 600;
                text-decoration: none;
                margin-top: 12px;
                transition: all 0.3s;
            }
            .meeting-btn:hover {
                transform: translateY(-3px);
                box-shadow: 0 6px 15px rgba(109, 40, 217, 0.25);
            }
        `
    };

    // Function to generate the vCard format for QR code
    const generateVCardData = () => {
        const { name, title, company, email, phone, companyUrl, linkedinUrl, location, companyAddress, bio } = profileData;
        
        let vCard = 'BEGIN:VCARD\nVERSION:3.0\n';
        if (name) vCard += `FN:${name}\n`;
        if (title) vCard += `TITLE:${title}\n`;
        if (company) vCard += `ORG:${company}\n`;
        if (email) vCard += `EMAIL:${email}\n`;
        if (phone) vCard += `TEL:${phone}\n`;
        if (companyAddress) vCard += `ADR:;;${companyAddress};;;;\n`;
        if (companyUrl) vCard += `URL:${companyUrl}\n`;
        if (linkedinUrl) vCard += `URL;type=LINKEDIN:${linkedinUrl}\n`;
        if (bio) vCard += `NOTE:${bio}\n`;
        vCard += 'END:VCARD';
        
        return vCard;
    };

    // Helper function to create a data URL from QR code
    const getQRCodeAsDataURL = (value) => {
        // Create a temporary element to render the QR code
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        document.body.appendChild(tempDiv);
        
        // Create a canvas element
        const canvas = document.createElement('canvas');
        const size = 200;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        
        // Draw white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, size, size);
        
        // Create QR code data
        const qrData = {
            text: value,
            width: size,
            height: size,
            colorDark: '#000000',
            colorLight: '#ffffff',
            correctLevel: 3 // QRErrorCorrectLevel.H
        };
        
        // Use QRious, qrcode.js, or similar library here
        // For simplicity, we'll use a base template with our data
        const qrCodeImg = new Image();
        qrCodeImg.crossOrigin = 'Anonymous';
        
        // Generate QR code data URL (this is just a placeholder - in real implementation, you'd use a QR code generation library)
        const qrCodeUrl = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMgAAADIAQAAAACFI5MzAAABp0lEQVR4Xu2WwW7DMAxD/f8/nR12GmApalNMjgxl9dYiNF9JSiJ58/bYi18kNxKWvK9/fQ8/IT+OhNB31NBo4kT+Dcnij7HKqiZhQgWckMsk9bSVJLvJjUgNSJ2yX9fzQnLJ06Vy5qVNW0lq5DYRIytC0udY/+PIzbP2SZW7nMm8Iko4kV/cWq0M1w6qHXxCG3L3qKnNJdpMoqauaGk3IdXkVK1pA7E+I3Fs/t2ESPI9NjvpNpGi+RVtsRLcuJvI0NfGZUWR7CSWUZk8fL2+Ec+mrmDJG0kfG43K46C9JNppQ3Pc0Yt5I1HSxjmvGpCSNbXW8u7ziZwma68QMZGSxSScmLyReKzTsTCNK7KRdA2A1UpQZiOR+mLfp+F6J/FNFIaR5Hcf/EvE1LG0jtVFu0lcbptIeR4/ZyOx7IzF2LWFZCfR0Z2OE0dPJJSZW+/kj2VzI3HQUQNtMIXgRjIXD2vwXMGNZBziVl2jNpF5ituL3xZyI/GxLs8jMzMTOZEzkpA4eeq68JnI43zsvT39NhH5VcLpLvqZXB7QfyCPxy8/z1bHV29dKwAAAABJRU5ErkJggg==`;
        
        // Set the source and draw the QR code on canvas when loaded
        qrCodeImg.onload = () => {
            ctx.drawImage(qrCodeImg, 0, 0, size, size);
        };
        qrCodeImg.src = qrCodeUrl;
        
        // Clean up
        document.body.removeChild(tempDiv);
        
        // Return data URL
        return canvas.toDataURL('image/png');
    };

    // Update preview content when format, template, or features change
    useEffect(() => {
        // Generate the basic content
        let baseContent = generateProfilePageContent(previewFormat);
        
        // Add the template styles to the content
        if (baseContent) {
            // Find where the <head> section ends
            const headEndIndex = baseContent.indexOf('</head>');
            
            if (headEndIndex !== -1) {
                // Insert the template CSS into the head section
                let styledContent = baseContent.slice(0, headEndIndex) + 
                    `<style>${templateStyles[selectedTemplate]}</style>` + 
                    baseContent.slice(headEndIndex);
                
                // Find a suitable place to insert additional content
                const bodyEndIndex = styledContent.indexOf('</body>');
                const footerIndex = styledContent.indexOf('<footer class="footer">');
                const insertIndex = footerIndex !== -1 ? footerIndex : bodyEndIndex;
                
                if (insertIndex !== -1) {
                    let additionalContent = '';
                    
                    // 1. Add QR code if enabled
                    if (includeQrCode) {
                        // Generate vCard data
                        const vCardData = generateVCardData();
                        
                        // Create the QR code HTML
                        additionalContent += `
                            <div class="qr-code-section">
                                <h3 style="margin-bottom: 15px; color: #4b5563;">Scan to Save Contact Information</h3>
                                <div style="display: flex; justify-content: center;">
                                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(vCardData)}" alt="Contact QR Code" style="width: 200px; height: 200px;">
                                </div>
                                <p style="margin-top: 15px; color: #6b7280; font-size: 0.9rem;">
                                    Scan this code to save my contact details to your phone
                                </p>
                            </div>
                        `;
                    }
                    
                    // 2. Add meeting scheduler if enabled
                    if (includeMeetingScheduler && meetingSchedulerUrl) {
                        additionalContent += `
                            <div class="meeting-scheduler-section">
                                <h3 style="margin-bottom: 15px; color: #4b5563;">Schedule a Meeting</h3>
                                <p style="margin-bottom: 15px; color: #6b7280;">
                                    Need to discuss something? Feel free to schedule a meeting with me.
                                </p>
                                <a href="${meetingSchedulerUrl}" target="_blank" class="meeting-btn">
                                    Schedule Now
                                </a>
                            </div>
                        `;
                    }
                    
                    // Insert the additional content
                    if (additionalContent) {
                        styledContent = styledContent.slice(0, insertIndex) + 
                            additionalContent + 
                            styledContent.slice(insertIndex);
                    }
                }
                
                setPreviewContent(styledContent);
            } else {
                // If no head section found, just use the base content
                setPreviewContent(baseContent);
            }
        }
    }, [previewFormat, profileData, generateProfilePageContent, selectedTemplate, includeQrCode, includeMeetingScheduler, meetingSchedulerUrl]);

    const handleFormatChange = (e) => {
        setPreviewFormat(e.target.value);
    };

    const refreshPreview = () => {
        // Generate the basic content
        let baseContent = generateProfilePageContent(previewFormat);
        
        // Add the template styles to the content
        if (baseContent) {
            // Find where the <head> section ends
            const headEndIndex = baseContent.indexOf('</head>');
            
            if (headEndIndex !== -1) {
                // Insert the template CSS into the head section
                let styledContent = baseContent.slice(0, headEndIndex) + 
                    `<style>${templateStyles[selectedTemplate]}</style>` + 
                    baseContent.slice(headEndIndex);
                
                // Find a suitable place to insert additional content
                const bodyEndIndex = styledContent.indexOf('</body>');
                const footerIndex = styledContent.indexOf('<footer class="footer">');
                const insertIndex = footerIndex !== -1 ? footerIndex : bodyEndIndex;
                
                if (insertIndex !== -1) {
                    let additionalContent = '';
                    
                    // 1. Add QR code if enabled
                    if (includeQrCode) {
                        // Generate vCard data
                        const vCardData = generateVCardData();
                        
                        // Create the QR code HTML
                        additionalContent += `
                            <div class="qr-code-section">
                                <h3 style="margin-bottom: 15px; color: #4b5563;">Scan to Save Contact Information</h3>
                                <div style="display: flex; justify-content: center;">
                                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(vCardData)}" alt="Contact QR Code" style="width: 200px; height: 200px;">
                                </div>
                                <p style="margin-top: 15px; color: #6b7280; font-size: 0.9rem;">
                                    Scan this code to save my contact details to your phone
                                </p>
                            </div>
                        `;
                    }
                    
                    // 2. Add meeting scheduler if enabled
                    if (includeMeetingScheduler && meetingSchedulerUrl) {
                        additionalContent += `
                            <div class="meeting-scheduler-section">
                                <h3 style="margin-bottom: 15px; color: #4b5563;">Schedule a Meeting</h3>
                                <p style="margin-bottom: 15px; color: #6b7280;">
                                    Need to discuss something? Feel free to schedule a meeting with me.
                                </p>
                                <a href="${meetingSchedulerUrl}" target="_blank" class="meeting-btn">
                                    Schedule Now
                                </a>
                            </div>
                        `;
                    }
                    
                    // Insert the additional content
                    if (additionalContent) {
                        styledContent = styledContent.slice(0, insertIndex) + 
                            additionalContent + 
                            styledContent.slice(insertIndex);
                    }
                }
                
                setPreviewContent(styledContent);
            } else {
                // If no head section found, just use the base content
                setPreviewContent(baseContent);
            }
        }
        
        showNotification('Preview refreshed');
    };

    const copyProfileContent = () => {
        navigator.clipboard.writeText(previewContent);
        showNotification('HTML content copied to clipboard');
    };

    const handleDownloadMenuOpen = (event) => {
        setDownloadMenuAnchor(event.currentTarget);
    };

    const handleDownloadMenuClose = () => {
        setDownloadMenuAnchor(null);
    };

    const handleTemplateMenuOpen = (event) => {
        setTemplateMenuAnchor(event.currentTarget);
    };

    const handleTemplateMenuClose = () => {
        setTemplateMenuAnchor(null);
    };

    const handleTemplateSelect = (templateId) => {
        setSelectedTemplate(templateId);
        setTemplateMenuAnchor(null);
        showNotification(`${templates.find(t => t.id === templateId).name} template applied`);
    };

    const showNotification = (message, severity = 'success') => {
        setNotification({
            open: true,
            message,
            severity
        });
    };

    const handleCloseNotification = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setNotification({
            ...notification,
            open: false
        });
    };

    const downloadProfilePage = (format) => {
        try {
            const filename = exportFilename || getProfileSlug();
            
            if (format === 'pdf') {
                // Handle PDF download
                downloadAsPDF(filename);
            } else {
                // Handle HTML or PHP download
                const content = previewContent; // Use the current styled content
                const blob = new Blob([content], {
                    type: format === 'html' ? 'text/html' : 'application/octet-stream'
                });
                const url = URL.createObjectURL(blob);
                const downloadLink = document.createElement('a');
                downloadLink.href = url;
                downloadLink.download = `${filename}.${format}`;
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
                URL.revokeObjectURL(url);
                
                showNotification(`Profile downloaded as ${format.toUpperCase()}: ${filename}.${format}`);
            }
            
            handleDownloadMenuClose();
        } catch (error) {
            console.error(`Error downloading profile page as ${format}:`, error);
            showNotification(`Error downloading file: ${error.message}`, 'error');
        }
    };

    // Function to download as PDF
    const downloadAsPDF = async (filename) => {
        try {
            // Show notification that PDF is being created
            showNotification('Creating PDF, please wait...', 'info');
            
            // Get the iframe content
            const iframe = iframeRef.current;
            if (!iframe) throw new Error('Preview not available');
            
            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
            
            // Use html2canvas to capture iframe content as canvas
            const canvas = await html2canvas(iframeDocument.body, {
                scale: 2, // Higher scale for better quality
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff'
            });
            
            // Create a PDF using jsPDF
            const imgWidth = 210; // A4 width in mm
            const pageHeight = 297; // A4 height in mm
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            
            // Create new jsPDF instance
            const pdf = new jsPDF('p', 'mm', 'a4');
            
            // Add the image to the PDF
            pdf.addImage(
                canvas.toDataURL('image/jpeg', 1.0), 
                'JPEG', 
                0, 
                0, 
                imgWidth, 
                imgHeight
            );
            
            // Handle multi-page content if needed
            let heightLeft = imgHeight - pageHeight;
            let position = -pageHeight; // Start position for the next page
            
            while (heightLeft > 0) {
                // Add a new page
                pdf.addPage();
                // Add the image to the new page, positioned to show the next portion
                pdf.addImage(
                    canvas.toDataURL('image/jpeg', 1.0),
                    'JPEG',
                    0,
                    position,
                    imgWidth,
                    imgHeight
                );
                
                // Update remaining height and position
                heightLeft -= pageHeight;
                position -= pageHeight;
            }
            
            // Save the PDF file
            pdf.save(`${filename}.pdf`);
            
            showNotification('PDF downloaded successfully');
        } catch (error) {
            console.error('Error generating PDF:', error);
            showNotification('Error generating PDF: ' + error.message, 'error');
        }
    };

    // Function to print preview
    const printPreview = () => {
        try {
            const iframe = iframeRef.current;
            if (!iframe) return;

            // Get the iframe content window
            const iframeWindow = iframe.contentWindow;
            if (!iframeWindow) {
                throw new Error('Cannot access preview content');
            }

            // Print the iframe content
            iframeWindow.focus();
            iframeWindow.print();
            showNotification('Print dialog opened');
        } catch (error) {
            console.error('Error printing preview:', error);
            showNotification('Error opening print dialog: ' + error.message, 'error');
        }
    };

    // Get preview container width based on device view
    const getPreviewWidth = () => {
        switch(deviceView) {
            case 'mobile':
                return '375px';
            case 'tablet':
                return '768px';
            case 'desktop':
            default:
                return '100%';
        }
    };

    // Common Card Props for consistent styling
    const cardProps = {
        elevation: 3,
        sx: {
            height: '100%',
            borderRadius: 2,
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 8,
            },
            '& .MuiCardContent-root': {
                height: '100%',
                p: 3
            }
        }
    };

    return (
        <Grid container spacing={3}>
            {/* HTML Preview Panel */}
            <Grid item xs={12}>
                <Card {...cardProps}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Profile Page Preview
                            </Typography>
                            <Tooltip title="Preview how your profile page will look when exported">
                                <IconButton size="small">
                                    <HelpCircle size={16} />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {/* Preview Controls */}
                        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Format</FormLabel>
                                    <RadioGroup
                                        value={previewFormat}
                                        onChange={handleFormatChange}
                                        row
                                    >
                                        <FormControlLabel value="html" control={<Radio />} label="HTML" />
                                        <FormControlLabel value="php" control={<Radio />} label="PHP" />
                                    </RadioGroup>
                                </FormControl>
                                
                                {/* Template Selector Button */}
                                <Button
                                    variant="outlined"
                                    onClick={handleTemplateMenuOpen}
                                    endIcon={<ChevronDown />}
                                    disabled={isTrial}
                                    sx={{ 
                                        ml: 2,
                                        borderColor: templates.find(t => t.id === selectedTemplate)?.color,
                                        color: templates.find(t => t.id === selectedTemplate)?.color
                                    }}
                                >
                                    {templates.find(t => t.id === selectedTemplate)?.name} Template
                                </Button>
                                <Menu
                                    anchorEl={templateMenuAnchor}
                                    open={Boolean(templateMenuAnchor)}
                                    onClose={handleTemplateMenuClose}
                                >
                                    {templates.map(template => (
                                        <MenuItem 
                                            key={template.id} 
                                            onClick={() => handleTemplateSelect(template.id)}
                                            selected={selectedTemplate === template.id}
                                        >
                                            <Box
                                                sx={{
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: '50%',
                                                    bgcolor: template.color,
                                                    mr: 1
                                                }}
                                            />
                                            {template.name}
                                            {selectedTemplate === template.id && (
                                                <CheckCircle size={16} style={{ marginLeft: 8 }} />
                                            )}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Mobile View">
                                    <IconButton 
                                        color={deviceView === 'mobile' ? 'primary' : 'default'}
                                        onClick={() => setDeviceView('mobile')}
                                    >
                                        <Phone />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Tablet View">
                                    <IconButton 
                                        color={deviceView === 'tablet' ? 'primary' : 'default'}
                                        onClick={() => setDeviceView('tablet')}
                                    >
                                        <Tablet />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Desktop View">
                                    <IconButton 
                                        color={deviceView === 'desktop' ? 'primary' : 'default'}
                                        onClick={() => setDeviceView('desktop')}
                                    >
                                        <Laptop />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Print Preview">
                                    <IconButton 
                                        onClick={printPreview}
                                        disabled={isTrial}
                                    >
                                        <Printer />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Refresh Preview">
                                    <IconButton onClick={refreshPreview}>
                                        <RefreshCw />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Additional options */}
                        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {/* Contact QR Code Option */}
                            <FormGroup sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Switch 
                                            checked={includeQrCode}
                                            onChange={(e) => setIncludeQrCode(e.target.checked)}
                                            disabled={isTrial}
                                        />
                                    }
                                    label="Include Contact QR Code"
                                />
                                <Tooltip title="Adds a QR code that people can scan to save your contact info">
                                    <IconButton size="small">
                                        <HelpCircle size={14} />
                                    </IconButton>
                                </Tooltip>
                            </FormGroup>

                            {/* Meeting Scheduler Option */}
                            <FormGroup sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Switch 
                                            checked={includeMeetingScheduler}
                                            onChange={(e) => setIncludeMeetingScheduler(e.target.checked)}
                                            disabled={isTrial}
                                        />
                                    }
                                    label="Include Meeting Scheduler"
                                />
                                <Tooltip title="Adds a button to schedule meetings with you (Calendly, Google Calendar, etc.)">
                                    <IconButton size="small">
                                        <HelpCircle size={14} />
                                    </IconButton>
                                </Tooltip>
                            </FormGroup>
                        </Box>

                        {/* Meeting Scheduler URL field */}
                        {includeMeetingScheduler && (
                            <Box sx={{ mb: 3 }}>
                                <TextField
                                    label="Meeting Scheduler URL"
                                    placeholder="https://calendly.com/your-username or https://calendar.app.google/encrypted-key or similar app"
                                    value={meetingSchedulerUrl}
                                    onChange={(e) => setMeetingSchedulerUrl(e.target.value)}
                                    fullWidth
                                    disabled={isTrial}
                                    InputProps={{
                                        startAdornment: <Calendar size={16} style={{ marginRight: 8, color: '#6b7280' }} />,
                                    }}
                                    helperText="Enter your Calendly, Google Calendar, or other meeting scheduler URL"
                                />
                            </Box>
                        )}

                        {/* Preview Container */}
                        <Box 
                            sx={{ 
                                border: '1px solid #e0e0e0', 
                                borderRadius: 2, 
                                mb: 3, 
                                height: '60vh',
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                overflow: 'auto',
                                position: 'relative',
                                bgcolor: '#f8f9fa'
                            }}
                        >
                            {/* Trial overlay */}
                            {isTrial && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        bgcolor: 'rgba(255, 255, 255, 0.6)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        zIndex: 10,
                                        backdropFilter: 'blur(2px)',
                                        borderRadius: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            bgcolor: 'white',
                                            p: 3,
                                            borderRadius: 2,
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                            maxWidth: '80%',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                                            Premium Feature
                                        </Typography>
                                        <Typography variant="body1" paragraph>
                                            HTML preview and export is available only in the full version.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => navigate('/profiles/upgrade')}
                                        >
                                            Upgrade Now
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            <Box 
                                sx={{ 
                                    width: getPreviewWidth(),
                                    height: '100%',
                                    transition: 'width 0.3s ease-in-out',
                                    boxShadow: deviceView !== 'desktop' ? '0 0 20px rgba(0,0,0,0.1)' : 'none' 
                                }}
                            >
                                {/* Using iframe for live preview */}
                                <iframe
                                    ref={iframeRef}
                                    srcDoc={previewContent}
                                    title="Profile Preview"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        border: 'none',
                                        borderRadius: deviceView !== 'desktop' ? '8px' : '0px',
                                        backgroundColor: 'white'
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Filename and Export Buttons */}
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Export Filename"
                                    value={exportFilename}
                                    onChange={(e) => setExportFilename(e.target.value)}
                                    fullWidth
                                    placeholder={getProfileSlug()}
                                    helperText={`Will be exported as: ${exportFilename || getProfileSlug()}.html/php/pdf`}
                                    disabled={isTrial}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<Copy />}
                                        onClick={copyProfileContent}
                                        disabled={isTrial}
                                        fullWidth
                                    >
                                        {isTrial ? "Upgrade to Copy" : "Copy HTML"}
                                    </Button>
                                    
                                    {/* Download Dropdown Button */}
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<Download />}
                                        endIcon={<ChevronDown />}
                                        onClick={handleDownloadMenuOpen}
                                        disabled={isTrial}
                                        fullWidth
                                    >
                                        {isTrial ? "Upgrade to Download" : "Download As"}
                                    </Button>
                                    
                                    {/* Download Format Menu */}
                                    <Menu
                                        anchorEl={downloadMenuAnchor}
                                        open={Boolean(downloadMenuAnchor)}
                                        onClose={handleDownloadMenuClose}
                                    >
                                        <MenuItem onClick={() => downloadProfilePage('html')}>
                                            <FileText size={16} style={{ marginRight: '8px' }} />
                                            HTML File
                                        </MenuItem>
                                        <MenuItem onClick={() => downloadProfilePage('php')}>
                                            <Code size={16} style={{ marginRight: '8px' }} />
                                            PHP File
                                        </MenuItem>
                                        <MenuItem onClick={() => downloadProfilePage('pdf')}>
                                            <File size={16} style={{ marginRight: '8px' }} />
                                            PDF Document
                                        </MenuItem>
                                    </Menu>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Info Box - Updated to include QR code information */}
                        <Box 
                            sx={{ 
                                mt: 3, 
                                p: 2, 
                                bgcolor: 'rgba(37, 99, 235, 0.1)', 
                                borderRadius: 2,
                                border: '1px solid rgba(37, 99, 235, 0.2)'
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                <FileText size={16} style={{ verticalAlign: 'text-bottom', marginRight: '8px' }} />
                                Export Information:
                            </Typography>
                            <Typography variant="body2">
                                • <strong>HTML:</strong> Standalone page that works without a server<br />
                                • <strong>PHP:</strong> Includes additional features like a view counter<br />
                                • <strong>PDF:</strong> Print-friendly document that can be shared easily<br />
                                • <strong>Contact QR Code:</strong> Adds a scannable code for saving your contact details<br />
                                • <strong>Meeting Scheduler:</strong> Adds a button for visitors to schedule meetings with you
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* Notification Snackbar */}
            <Snackbar 
                open={notification.open} 
                autoHideDuration={4000} 
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseNotification} 
                    severity={notification.severity} 
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Grid>
    );
};

export default HtmlPreviewTab;